<template>
  <div class="permission-manager space-y-6 animate-[fadeIn_0.6s_ease-out]">
    <!-- 页面标题 -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F5D76E] to-[#F2C94C] flex items-center justify-center shadow-sm">
          <svg class="w-5 h-5 text-[#1B3C59]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <div>
          <h3 class="text-xl font-black text-slate-900">权限管理</h3>
        </div>
      </div>
      <button
        v-if="isSuperAdmin && hasPerm(PERMISSIONS.CREATE_ADMIN)"
        @click="handleAdd"
        class="flex items-center gap-1.5 px-4 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        新增管理员
      </button>
    </div>

    <!-- 前台关联提示 -->
    <div class="bg-blue-50/80 backdrop-blur-sm border border-blue-100/60 rounded-2xl p-4 flex items-start gap-3 text-sm text-blue-700">
      <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div>
        <p class="font-medium mb-1">前台关联</p>
        <p class="text-blue-600">权限配置决定不同角色在后台可操作的范围，以及前台部分功能的可见性</p>
      </div>
    </div>

    <!-- 管理员列表 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <el-table :data="adminList" stripe size="small" style="width: 100%">
        <el-table-column prop="username" label="账号名" min-width="120" />
        <el-table-column label="角色" width="120" align="center">
          <template #default="{ row }">
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 text-xs rounded font-medium',
                row.role === ROLES.SUPER_ADMIN
                  ? 'bg-purple-50 text-purple-600'
                  : 'bg-amber-50 text-amber-600'
              ]"
            >
              {{ roleLabel(row.role) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="权限数量" width="100" align="center">
          <template #default="{ row }">
            <span class="text-sm text-slate-600">{{ row.permissions?.length || 0 }} 项</span>
          </template>
        </el-table-column>
        <el-table-column label="数据范围" width="130" align="center">
          <template #default="{ row }">
            <span class="text-sm text-slate-600">{{ row.dataScope === 'all' ? '全部内容' : '仅自己创建' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template #default="{ row }">
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 text-xs rounded font-medium',
                row.status === 'active'
                  ? 'bg-emerald-50 text-emerald-600'
                  : 'bg-red-50 text-red-500'
              ]"
            >
              {{ row.status === 'active' ? '启用' : '禁用' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template #default="{ row }">
            <div class="flex items-center gap-2">
              <button
                v-if="canEdit(row)"
                @click="handleEdit(row)"
                class="text-[#F2C94C] hover:text-[#E5B73B] text-sm font-medium transition-colors"
              >
                编辑权限
              </button>
              <button
                v-if="canToggleStatus(row)"
                @click="handleToggleStatus(row)"
                :class="[
                  'text-sm font-medium transition-colors',
                  row.status === 'active' ? 'text-red-400 hover:text-red-500' : 'text-emerald-500 hover:text-emerald-600'
                ]"
              >
                {{ row.status === 'active' ? '禁用' : '启用' }}
              </button>
              <button
                v-if="canDelete(row)"
                @click="handleDelete(row)"
                class="text-red-400 hover:text-red-500 text-sm font-medium transition-colors"
              >
                删除
              </button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="adminList.length === 0" class="text-center py-12">
        <svg class="w-12 h-12 text-slate-200 mx-auto mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <div class="text-slate-400 text-sm">暂无管理员</div>
      </div>
    </div>

    <!-- 创建/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="720px"
      :close-on-click-modal="false"
      @closed="resetForm"
    >
      <!-- 创建时显示账号信息 -->
      <div v-if="!isEdit" class="mb-5">
        <el-form :model="form" label-width="100px" size="small">
          <el-form-item label="账号名" required>
            <el-input v-model="form.username" placeholder="请输入账号名" />
          </el-form-item>
          <el-form-item label="初始密码" required>
            <el-input v-model="form.password" type="password" placeholder="请输入初始密码" />
          </el-form-item>
        </el-form>
        <div class="border-t border-slate-100 my-4"></div>
      </div>

      <div class="flex gap-6">
        <!-- 左侧：权限分组 -->
        <div class="flex-1">
          <div class="flex items-center gap-2 mb-4">
            <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h4 class="text-sm font-black text-slate-900">功能权限</h4>
          </div>
          <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

          <div v-for="group in PERMISSION_GROUPS" :key="group.label" class="mb-4">
            <div class="flex items-center gap-2 mb-2">
              <el-checkbox
                :indeterminate="isIndeterminate(group)"
                :value="isGroupChecked(group)"
                @change="checked => toggleGroup(group, checked)"
              >
                <span class="font-medium text-sm text-slate-800">{{ group.label }}</span>
              </el-checkbox>
            </div>
            <div class="pl-6 space-y-1">
              <el-checkbox
                v-for="perm in group.permissions"
                :key="perm.key"
                v-model="form.permissions"
                :label="perm.key"
                class="block"
              >
                <span class="text-sm text-slate-600">{{ perm.label }}</span>
              </el-checkbox>
            </div>
          </div>
        </div>

        <!-- 右侧：数据范围 -->
        <div class="w-44 flex-shrink-0">
          <div class="flex items-center gap-2 mb-4">
            <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
            </svg>
            <h4 class="text-sm font-black text-slate-900">数据范围</h4>
          </div>
          <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

          <div class="space-y-2">
            <label
              v-for="opt in dataScopeOptions"
              :key="opt.value"
              class="flex items-start gap-2 p-2 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
              :class="form.dataScope === opt.value ? 'bg-slate-50' : ''"
            >
              <input
                v-model="form.dataScope"
                type="radio"
                :value="opt.value"
                class="mt-0.5"
              />
              <div>
                <div class="text-sm font-medium text-slate-700">{{ opt.label }}</div>
                <div class="text-xs text-slate-400 mt-0.5">{{ opt.desc }}</div>
              </div>
            </label>
          </div>
        </div>
      </div>

      <span slot="footer">
        <button
          @click="dialogVisible = false"
          class="px-4 py-2 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors mr-2"
        >
          取消
        </button>
        <button
          @click="handleSave"
          class="px-5 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
        >
          保存
        </button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { logAction } from '../../services/audit'
import { ROLES, PERMISSIONS, PERMISSION_GROUPS } from '../../data/adminConfig'

const DEFAULT_FORM = {
  id: '',
  username: '',
  password: '',
  role: ROLES.ADMIN,
  permissions: [],
  dataScope: 'own',
  status: 'active',
}

export default {
  name: 'PermissionManager',

  data() {
    return {
      dialogVisible: false,
      dialogTitle: '新增管理员',
      isEdit: false,
      form: { ...DEFAULT_FORM },
      PERMISSION_GROUPS,
      PERMISSIONS,
      ROLES,
      dataScopeOptions: [
        { value: 'all', label: '全部内容', desc: '能看到系统里所有数据' },
        { value: 'own', label: '仅自己创建', desc: '只能看到自己在后台创建的内容' },
      ],
    }
  },

  computed: {
    ...mapState(['admins']),
    ...mapGetters(['hasPermission', 'isSuperAdmin', 'currentAdminId']),

    adminList() {
      return this.admins || []
    },
  },

  methods: {
    roleLabel(role) {
      const labels = {
        [ROLES.SUPER_ADMIN]: '超级管理员',
        [ROLES.ADMIN]: '普通管理员',
        [ROLES.USER]: '普通用户',
      }
      return labels[role] || role
    },

    canEdit(row) {
      if (!this.isSuperAdmin) return false
      if (row.role === ROLES.SUPER_ADMIN) return false
      return this.hasPerm(PERMISSIONS.EDIT_ADMIN)
    },

    canToggleStatus(row) {
      if (!this.isSuperAdmin) return false
      if (row.role === ROLES.SUPER_ADMIN) return false
      return this.hasPerm(PERMISSIONS.EDIT_ADMIN)
    },

    canDelete(row) {
      if (!this.isSuperAdmin) return false
      if (row.role === ROLES.SUPER_ADMIN) return false
      return this.hasPerm(PERMISSIONS.DELETE_ADMIN)
    },

    hasPerm(permission) {
      return this.hasPermission(permission)
    },

    isGroupChecked(group) {
      return group.permissions.every(p => this.form.permissions.includes(p.key))
    },

    isIndeterminate(group) {
      const checked = group.permissions.filter(p => this.form.permissions.includes(p.key)).length
      return checked > 0 && checked < group.permissions.length
    },

    toggleGroup(group, checked) {
      const keys = group.permissions.map(p => p.key)
      if (checked) {
        this.form.permissions = [...new Set([...this.form.permissions, ...keys])]
      } else {
        this.form.permissions = this.form.permissions.filter(k => !keys.includes(k))
      }
    },

    handleAdd() {
      this.isEdit = false
      this.dialogTitle = '新增管理员'
      this.form = { ...DEFAULT_FORM }
      this.dialogVisible = true
    },

    handleEdit(row) {
      this.isEdit = true
      this.dialogTitle = `编辑权限 - ${row.username}`
      this.form = {
        id: row.id,
        username: row.username,
        password: '',
        role: row.role,
        permissions: [...(row.permissions || [])],
        dataScope: row.dataScope || 'own',
        status: row.status,
      }
      this.dialogVisible = true
    },

    handleToggleStatus(row) {
      const newStatus = row.status === 'active' ? 'disabled' : 'active'
      const actionText = newStatus === 'active' ? '启用' : '禁用'

      this.$confirm(`确定${actionText}管理员「${row.username}」？`, '确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        const updated = this.admins.map(a =>
          a.id === row.id ? { ...a, status: newStatus } : a
        )
        this.$store.commit('SET_ADMINS', updated)

        logAction({
          action: 'UPDATE',
          targetType: 'admin',
          targetId: row.id,
          reason: `${actionText}管理员账号: ${row.username}`,
          source: 'admin-ui',
          actor: { role: this.$store.state.userRole },
        })

        this.$message.success(`${actionText}成功`)
      }).catch(() => {})
    },

    handleDelete(row) {
      // 安全规则：至少保留一个超级管理员
      const superAdmins = this.admins.filter(a => a.role === ROLES.SUPER_ADMIN)
      if (row.role === ROLES.SUPER_ADMIN && superAdmins.length <= 1) {
        this.$message.error('系统中至少需要保留一个超级管理员')
        return
      }

      this.$confirm(`确定删除管理员「${row.username}」？此操作不可恢复。`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        const updated = this.admins.filter(a => a.id !== row.id)
        this.$store.commit('SET_ADMINS', updated)

        logAction({
          action: 'DELETE',
          targetType: 'admin',
          targetId: row.id,
          reason: `删除管理员: ${row.username}`,
          source: 'admin-ui',
          actor: { role: this.$store.state.userRole },
        })

        this.$message.success('删除成功')
      }).catch(() => {})
    },

    handleSave() {
      if (!this.isEdit) {
        if (!this.form.username.trim()) {
          this.$message.error('请输入账号名')
          return
        }
        if (!this.form.password.trim()) {
          this.$message.error('请输入初始密码')
          return
        }
        // 检查账号名是否重复
        if (this.admins.some(a => a.username === this.form.username.trim())) {
          this.$message.error('该账号名已存在')
          return
        }
      }

      const updated = [...this.admins]

      if (this.isEdit) {
        const idx = updated.findIndex(a => a.id === this.form.id)
        if (idx !== -1) {
          updated[idx] = {
            ...updated[idx],
            permissions: [...this.form.permissions],
            dataScope: this.form.dataScope,
          }
        }

        logAction({
          action: 'UPDATE',
          targetType: 'admin',
          targetId: this.form.id,
          reason: `编辑管理员权限: ${this.form.username}`,
          source: 'admin-ui',
          actor: { role: this.$store.state.userRole },
        })

        this.$message.success('权限更新成功')
      } else {
        const newAdmin = {
          id: `admin-${Date.now()}`,
          username: this.form.username.trim(),
          password: this.form.password,
          role: ROLES.ADMIN,
          permissions: [...this.form.permissions],
          dataScope: this.form.dataScope,
          status: 'active',
          createdAt: Date.now(),
        }
        updated.push(newAdmin)

        logAction({
          action: 'CREATE',
          targetType: 'admin',
          targetId: newAdmin.id,
          reason: `创建管理员: ${newAdmin.username}`,
          source: 'admin-ui',
          actor: { role: this.$store.state.userRole },
        })

        this.$message.success('管理员创建成功')
      }

      this.$store.commit('SET_ADMINS', updated)
      this.dialogVisible = false
    },

    resetForm() {
      this.form = { ...DEFAULT_FORM }
    },
  },
}
</script>
